import { postForm } from '@/shared/api/client';
import { submitForm } from '@/shared/api/submit';
import { deleteFile, toUploadedFile, uploadImage, type RawFile, type UploadedFile } from '@/shared/api/upload';
import { getListRows } from '@/shared/lib/pagination';
import { normalizeOptions } from '@/shared/lib/normalizeOptions';
import type { ListParams, ListResponse } from '@/shared/ui/list/types';

/** Loyalty lists read `page` from the query string (legacy `?page=`). */
export const fetchLoyaltyLevels = ({ page }: ListParams) =>
  postForm<ListResponse>('/customer/loyalty-level/list/', {}, { params: { page } });

export const fetchPrivileges = ({ page, level_id }: ListParams) =>
  postForm<ListResponse>('/customer/loyalty-privilege/list/', { page }, { params: { level_id } });

export async function fetchLevelOptions() {
  const res = await postForm<ListResponse>('/customer/loyalty-level/list/', {});
  return normalizeOptions(getListRows(res, ['data']));
}

export const deleteLoyaltyLevel = (id: number) => submitForm('/customer/loyalty-level/delete/', { id });

export const deletePrivilege = (id: number) => submitForm('/customer/loyalty-privilege/delete/', { id });

// ---- level create / edit ---------------------------------------------------

export interface LevelValues {
  title: string;
  minPaymentsAmount: string;
  iconColor: string;
  color: string;
  textColor: string;
  accentColor: string;
  cardBackground: string;
  cardTextColor: string;
}

export const EMPTY_LEVEL: LevelValues = {
  title: '',
  minPaymentsAmount: '',
  iconColor: '',
  color: '',
  textColor: '',
  accentColor: '',
  cardBackground: '',
  cardTextColor: '',
};

export interface LoyaltyLevelItem extends Record<string, unknown> {
  id: number;
  title?: string;
  min_payments_amount?: number | string;
  icon_color?: string;
  color?: string;
  text_color?: string;
  accent_color?: string;
  card_background?: string;
  card_text_color?: string;
}

export async function fetchLoyaltyLevel(id: number) {
  const res = await postForm<{ data?: LoyaltyLevelItem }>('/customer/loyalty-level/item/', {}, { params: { id } });
  return res.data;
}

export const saveLoyaltyLevel = (id: number | undefined, v: LevelValues) =>
  submitForm<{ success?: number; data?: { id: number } }>(
    id ? '/customer/loyalty-level/update/' : '/customer/loyalty-level/create/',
    {
      id,
      title: v.title,
      min_payments_amount: Number(v.minPaymentsAmount),
      color: v.color,
      accent_color: v.accentColor,
      text_color: v.textColor,
      icon_color: v.iconColor,
      card_text_color: v.cardTextColor,
      card_background: v.cardBackground,
    },
  );

// ---- privilege create / edit -----------------------------------------------

export const PRIVILEGE_TYPES = [
  { value: '1', label: 'Акция' },
  { value: '2', label: 'Услуга' },
  { value: '3', label: 'Кэшбэк' },
  { value: '4', label: 'Кэшбэк за запчасти' },
];

export const CASHBACK_TYPE = '3';
/** Legacy create form preselects "Услуга". */
export const DEFAULT_PRIVILEGE_TYPE = '2';

export interface PrivilegeValues {
  name: string;
  description: string;
  typeId: string;
  value: string;
  label: string;
  shortDescription: string;
  levelIds: string[];
  formActive: boolean;
  formTitle: string;
  image: UploadedFile[];
}

export const EMPTY_PRIVILEGE: PrivilegeValues = {
  name: '',
  description: '',
  typeId: DEFAULT_PRIVILEGE_TYPE,
  value: '',
  label: '',
  shortDescription: '',
  levelIds: [],
  formActive: false,
  formTitle: '',
  image: [],
};

export interface PrivilegeItem extends Record<string, unknown> {
  id: number;
  name?: string;
  description?: string;
  /** Type title, e.g. "Услуга". */
  type?: string;
  value?: number | string | null;
  label?: string;
  short_description?: string;
  levels?: { id: number }[] | null;
  form_active?: number | boolean;
  form_title?: string;
  /** `[]` when the privilege has no image. */
  image?: RawFile | RawFile[] | null;
}

export async function fetchPrivilege(id: number) {
  const res = await postForm<{ data?: PrivilegeItem }>('/customer/loyalty-privilege/item/', {}, { params: { id } });
  return res.data;
}

export const privilegeToValues = (item: PrivilegeItem): PrivilegeValues => {
  const image = toUploadedFile(item.image);
  return {
    name: item.name ?? '',
    description: item.description ?? '',
    typeId: PRIVILEGE_TYPES.find((type) => type.label === item.type)?.value ?? '',
    value: item.value == null ? '' : String(item.value),
    label: item.label ?? '',
    shortDescription: item.short_description ?? '',
    levelIds: (item.levels ?? []).map((level) => String(level.id)),
    formActive: Boolean(item.form_active),
    formTitle: item.form_title ?? '',
    image: image ? [image] : [],
  };
};

export const uploadPrivilegeImage = (file: File) => uploadImage('/customer/loyalty-privilege/upload-image/', file);

export const deletePrivilegeImage = (id: number) => deleteFile('/customer/loyalty-privilege/delete-image/', id);

export const savePrivilege = (id: number | undefined, v: PrivilegeValues) =>
  submitForm<{ success?: number; data?: { id: number } }>(
    id ? '/customer/loyalty-privilege/update/' : '/customer/loyalty-privilege/create/',
    {
      id,
      name: v.name,
      description: v.description,
      type_id: v.typeId,
      label: v.label,
      short_description: v.shortDescription,
      image_id: v.image[0]?.id,
      value: v.typeId === CASHBACK_TYPE ? v.value : undefined,
      form_active: v.formActive,
      form_title: v.formActive ? v.formTitle : '',
      level_ids: v.levelIds,
    },
  );
